import React, {useEffect, useRef} from "react";
import MaterialTable from "@material-table/core";
import {Box, Grid, TextField, Tooltip, Typography} from "@mui/material";
import get from "lodash.get";
import {isNotInsurerReadOnlyUser} from "../../constants/UserAuth";

const EditableRemark = (props) => {
    const {
        cellValue,
        categoryIndex,
        rowIndex,
        updateFunction // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = React.useState(cellValue)
    const onChange = e => {
        setValue(e.target.value)
    }

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        updateFunction(categoryIndex, rowIndex, value)
    }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cellValue)
    }, [cellValue])

    return <Tooltip title={value}><TextField
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        variant='outlined'
        size='small'
        fullWidth
        style={{background: '#ffffff'}}
        disabled={!isNotInsurerReadOnlyUser()}
        InputProps={{
            style: {fontSize: '0.8rem'}
        }}/></Tooltip>
}

const EditableFinalAmount = (props) => {
    const {
        cellValue,
        categoryIndex,
        rowIndex,
        updateFunction // This is a custom function that we supplied to our table instance
    } = props
    // We need to keep and update the state of the cell normally
    const [value, setValue] = React.useState(cellValue)
    const onChange = e => {
        setValue(e.target.value)
    }

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        updateFunction(categoryIndex, rowIndex, value)
    }

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
        setValue(cellValue)
    }, [cellValue])

    return <TextField
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        variant='outlined'
        type="number"
        size='small'
        fullWidth
        style={{background: '#ffffff'}}
        InputProps={{
            style: {fontSize: '0.8rem'}
        }}/>
}

// material-table warns (and can't keep row state between renders) when rows have no `id`.
// Fall back to the row's position, which is what material-table uses internally anyway.
const withRowIds = (rows = []) => rows.map((row, index) => (row?.id === undefined ? {...row, id: index} : row))

export default function CustomTable(props) {
    const {
        title = null,
        tableColumns,
        tableData = [],
        onRowClick = null,
        isPagination = true,
        isDownload = false,
        collectForRerun,
        selectedRows,
        selection = false,
        noStickyHeader = false,
        isSearch = false,
        isPML = false,
        showRoomProportional = true,
        layout = "auto",
        headerStyle = {},
        setEditedPMLRows = () => {
        }
    } = props;

    const tableRef = useRef(null);

    const [pmlCategoryData, setPmlCategoryData] = React.useState(tableData)
    const [pmlLineItemData, setPmlLineItemData] = React.useState([])

    React.useEffect(() => {
        if (!isPML) {
            return
        }
        const filteredCategoryData = tableData.map(({billItemList, ...rest}) => rest)
        setPmlCategoryData(filteredCategoryData)
        const filteredBillItemData = tableData.map((item, index) => item?.billItemList?.map(billItem => ({categoryIndex: index, ...billItem})))
        setPmlLineItemData(filteredBillItemData)
    }, [])

    const updateEditedPMLRows = (categoryIndex, billLineIndex, master_category, final_amount, remarks, bill_item_name) => {
        setEditedPMLRows(prevData => {
            const prevIndex = prevData.findIndex(item => item.categoryIndex === categoryIndex && item.billLineIndex === billLineIndex)
            const updatedData = [...prevData]; // Create a copy of the previous data array

            if (prevIndex === -1) {
                updatedData.push({
                    categoryIndex,
                    billLineIndex,
                    value: {
                        lineItem: bill_item_name,
                        masterCategory: master_category,
                        insurerFinalAmount: final_amount,
                        insurerRemarks: remarks
                    }
                });
            } else {
                updatedData[prevIndex] = {
                    ...updatedData[prevIndex], // Copy the existing object
                    value: {
                        lineItem: bill_item_name,
                        masterCategory: master_category,
                        insurerFinalAmount: final_amount,
                        insurerRemarks: remarks
                    }
                };
            }
            return updatedData;
        });
    }

    const updateRemarks = (categoryIndex, billLineIndex, remark) => {
        setPmlLineItemData(prevData => (
            prevData.map((item, index) => (
                index !== categoryIndex
                    ? item
                    : item.map((row, rowIndex) => (
                        rowIndex !== billLineIndex
                            ? row
                            : {...row, remarks: remark}
                    ))
            ))
        ))
        setPmlCategoryData(prevData => (
            prevData.map((item, index) => (
                index !== categoryIndex
                    ? item
                    : {...item, tableData: {...item.tableData, showDetailPanel: tableRef?.current?.props?.detailPanel}}
            ))
        ))
        updateEditedPMLRows(
            categoryIndex,
            billLineIndex,
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'master_category']),
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'final_amount']),
            remark,
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'bill_item_name']),
        )
    }

    const updateFinalAmount = (categoryIndex, billLineIndex, finalAmount) => {
        setPmlLineItemData(prevData => (
            prevData.map((item, index) => (
                index !== categoryIndex
                    ? item
                    : item.map((row, rowIndex) => (
                        rowIndex !== billLineIndex
                            ? row
                            : {...row, final_amount: parseFloat(finalAmount)}
                    ))
            ))
        ))
        const updatedFinalAmount = pmlLineItemData[categoryIndex].reduce((agg, row, index) => agg + (index === billLineIndex ? parseFloat(finalAmount) : row.final_amount), 0)
        setPmlCategoryData(prevData => (
            prevData.map((item, index) => (
                index !== categoryIndex
                    ? item
                    : {
                        ...item,
                        finalAmount: updatedFinalAmount,
                        tableData: {...item.tableData, showDetailPanel: tableRef?.current?.props?.detailPanel}
                    }
            ))
        ))
        updateEditedPMLRows(
            categoryIndex,
            billLineIndex,
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'master_category']),
            parseFloat(finalAmount),
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'remarks']),
            get(pmlLineItemData, [categoryIndex, billLineIndex, 'bill_item_name']),
        )
    }

    let pmlInnerColumns = [
        {
            title: "Bill Line Item ",
            field: "bill_item_name",
            sorting: false,
            align: "center",
            width: '280px',
            render: (rowData) =>
                rowData.bill_item_name && (
                    <Typography varient="p" fontSize={"14px"}>
                        {rowData.bill_item_name}
                    </Typography>
                ),
        },
        {
            title: "",
            field: "actual_amount",
            sorting: true,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.actual_amount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.actual_amount)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        {" "}
                        &#x20b9; 0
                    </Typography>
                ),
        },
        {
            title: "",
            field: "room_proportional_discount",
            sorting: false,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData &&
                showRoomProportional === true &&
                rowData.room_proportional_discount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        {rowData.room_proportional_discount}{" "}
                        &#x25;
                    </Typography>
                ) : (
                    showRoomProportional === true && (
                        <Typography varient="p" fontSize={"14px"}>
                            0{" "}&#x25;
                        </Typography>
                    )
                ),
        },

        {
            title: "",
            field: "amount_after_room_proportional_discount",
            sorting: true,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData &&
                showRoomProportional === true &&
                rowData.amount_after_room_proportional_discount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.amount_after_room_proportional_discount)}
                    </Typography>
                ) : (
                    showRoomProportional === true && (
                        <Typography varient="p" fontSize={"14px"}>
                            {" "}
                            &#x20b9;{" "}0
                        </Typography>
                    )
                ),
        },
        {
            title: "",
            field: "mou_discount",
            sorting: false,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.mou_discount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        {Math.trunc(rowData.mou_discount * 100) / 100}{" "}
                        &#x25;
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        0{" "}&#x25;
                    </Typography>
                ),
        },
        {
            title: "",
            field: "amount_after_mou_discount",
            sorting: true,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.amount_after_mou_discount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.amount_after_mou_discount)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}0
                    </Typography>
                ),
        },

        {
            title: "",
            field: "limit",
            sorting: false,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.limit !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.limit)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}0
                    </Typography>
                ),
        },
        {
            title: "",
            field: "amountBeforeCopay",
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.amountBeforeCopay !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.amountBeforeCopay)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}0
                    </Typography>
                ),
        },
        {
            title: "",
            field: "copay",
            sorting: false,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData.copay !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        {Math.trunc(rowData.copay)}{" "}&#x25;
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        0{" "}&#x25;
                    </Typography>
                ),
        },
        {
            title: "",
            field: "final_amount",
            sorting: true,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData?.final_amount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.final_amount)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}0
                    </Typography>
                )
        },
        {
            title: "",
            field: "finalAmountSentToJarvis",
            sorting: true,
            align: "right",
            headerStyle: {
                width: '140px'
            },
            width: '140px',
            render: (rowData) =>
                rowData && rowData?.final_amount_after_drop !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}
                        {Math.trunc(rowData.final_amount_after_drop)}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9;{" "}0
                    </Typography>
                )
        },
        {
            title: "",
            field: "remarks",
            width: '250px',
            render: (rowData) => {
                return (
                    <EditableRemark
                        cellValue={(rowData.remarks || '').replaceAll('\n', ',') || ''}
                        categoryIndex={rowData?.categoryIndex}
                        rowIndex={rowData?.tableData?.index}
                        updateFunction={updateRemarks}
                    />
                )
            }
        },
         {
        title: "",
        field: "cm_small_discount_percentage",
        align: "right",
        headerStyle: {
            width: '200px'
        },
        width: '200px',
        render: (rowData) =>
            rowData && rowData.cm_small_discount_percentage !== null ? (
                <Typography varient="p" fontSize={"14px"}>
                    {Math.trunc(rowData.cm_small_discount_percentage)} &#x25;{" "}
                </Typography>
            ) : (
                <Typography varient="p" fontSize={"14px"}>
                    0 &#x25;
                </Typography>
            ),
    },
    {
        title: "",
        field: "amount_after_cm_discount",
        align: "right",
        headerStyle: {
            width: '200px'
        },
        width: '200px',
        render: (rowData) =>
            rowData && rowData.amount_after_cm_discount !== null ? (
                <Typography varient="p" fontSize={"14px"}>
                    &#x20b9; {Math.trunc(rowData.amount_after_cm_discount)}{" "}
                </Typography>
            ) : (
                <Typography varient="p" fontSize={"14px"}>
                    &#x20b9; 0
                </Typography>
            ),
    },
    {
        title: "",
        field: "spot_discount_percentage",
        align: "right",
        headerStyle: {
            width: '200px'
        },
        width: '200px',
        render: (rowData) =>
            rowData && rowData.spot_discount_percentage !== null ? (
                <Typography varient="p" fontSize={"14px"}>
                    {Math.trunc(rowData.spot_discount_percentage)} &#x25;{" "}
                </Typography>
            ) : (
                <Typography varient="p" fontSize={"14px"}>
                    0 &#x25;
                </Typography>
            ),
    },
    {
        title: "",
        field: "amount_after_spot_discount",
        align: "right",
        headerStyle: {
            width: '200px'
        },
        width: '200px',
        render: (rowData) =>
            rowData && rowData.amount_after_spot_discount !== null ? (
                <Typography varient="p" fontSize={"14px"}>
                    &#x20b9; {Math.trunc(rowData.amount_after_spot_discount)}{" "}
                </Typography>
            ) : (
                <Typography varient="p" fontSize={"14px"}>
                    &#x20b9; 0
                </Typography>
            ),
    },
    {
            title: "",
            field: "amount_after_discount",
            align: "right",
            headerStyle: {
                width: '200px'
            },
            width: '200px',
            render: (rowData) =>
                rowData && rowData.amount_after_discount !== null ? (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9; {Math.trunc(rowData.amount_after_discount)}{" "}
                    </Typography>
                ) : (
                    <Typography varient="p" fontSize={"14px"}>
                        &#x20b9; 0
                    </Typography>
                ),
        }
    ];
    useEffect(() => {
        tableData &&
        tableData.length > 0 &&
        tableRef.current.onGroupExpandChanged([0]);
    }, [tableData]);

    if (!showRoomProportional) {
        pmlInnerColumns = pmlInnerColumns.filter(
            (x) =>
                x.field !== "room_proportional_discount" &&
                x.field !== "amount_after_room_proportional_discount"
        );
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column"}}>
            <Box>

                <MaterialTable
                    tableRef={tableRef}
                    options={{
                        padding: "dense",
                        toolbar: isSearch,
                        paging: false,
                        draggable: false,
                        search: isSearch,
                        paginationPosition: "bottom",
                        emptyRowsWhenPaging: false,
                        tableLayout: isPML ? 'fixed' : layout,
                        selection: selection,
                        /*
                         * A sticky header with no background of its own lets the rows
                         * scroll through it. material-table puts headerStyle inline on
                         * the th, which outranks the theme's MuiTableCell-head class, so
                         * the opaque fill has to be declared here. Colour mirrors
                         * MuiTheme.js COLOR.headerBg; zIndex clears the selection column.
                         */
                        headerStyle: {
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            fontWeight: "bold",
                            ...headerStyle,
                        },
                        tableWidth: 'full',
                        maxBodyHeight: noStickyHeader === false && !isPML && "1200px",
                        overflowX: "scroll",
                        overflowY: "visible",
                        rowStyle: (rowData) => {
                            return {
                                background: get(rowData, ["color"], "#FFFFFF"),
                            };
                        },
                    }}
                    title={title}
                    columns={tableColumns}
                    data={withRowIds(isPML ? pmlCategoryData : (tableData.map((row) =>
                        selectedRows?.find((selected) => selected.id === row.id)
                            ? {...row, tableData: {checked: true}}
                            : row
                    )))}
                    onRowClick={onRowClick}
                    onSelectionChange={(rowData) => {
                        collectForRerun(rowData);
                    }}
                    localization={{
                        toolbar: {
                            searchPlaceholder: "Enter data to search",
                            emptyDataSourceMessage: "No Data Found",
                            nRowsSelected: "",
                        },
                        pagination: {
                            labelRowsPerPage: "Claims to display",
                        },
                    }}
                    renderSummaryRow={isPML ? ({column, data}) => {
                        if (column.field === 'category_name') {
                            return {
                                value: `Total`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'tariffAmount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.tariffAmount, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amountAfterRoomProportionalDiscount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.amountAfterRoomProportionalDiscount, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amountAfterMou') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.amountAfterMou, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amountBeforeCopay') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.amountBeforeCopay, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'finalAmount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.finalAmount, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'finalAmountSentToJarvis') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + row.amountAfterDrop, 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amount_after_cm_discount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + (Number(row.amount_after_cm_discount) || 0), 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amount_after_spot_discount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + (Number(row.amount_after_spot_discount) || 0), 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        } else if (column.field === 'amount_after_discount') {
                            return {
                                value: `₹ ${Math.trunc(data.reduce((agg, row) => agg + (Number(row.amount_after_discount) || 0), 0)).toLocaleString('en-IN')}`,
                                style: {background: "white", fontWeight: 'bold'},
                            }
                        }
                        return undefined
                    } : null}
                    detailPanel={
                        isPML === true
                            ? (rowData) => {
                                return (
                                    <Grid>
                                        <MaterialTable
                                            options={{
                                                padding: "dense",
                                                toolbar: false,
                                                paging: false,
                                                draggable: false,
                                                search: false,
                                                showTitle: false,
                                                // same opaque sticky header as the outer table
                                                headerStyle: {
                                                    position: "sticky",
                                                    top: "0",
                                                    zIndex: 2,
                                                    fontWeight: "bold",
                                                    fontSize: "20px !important",
                                                    backgroundColor: "#F7F9FB",
                                                    backgroundClip: "padding-box",
                                                },
                                                overflowX: "auto",
                                            }}
                                            data={withRowIds(
                                                rowData?.rowData?.tableData?.id !== null
                                                    ? pmlLineItemData[rowData.rowData.tableData.id]
                                                    : []
                                            )}
                                            columns={pmlInnerColumns}
                                            // detailPanel={[
                                            //   {
                                            //     icon: "  ",
                                            //     disabled: true,
                                            //     // isFreeAction: true,
                                            //     render: () => <></>,
                                            //   },
                                            // ]}
                                        />
                                    </Grid>
                                )
                            }
                            : null
                    }
                />
            </Box>
        </Box>
    );
}
